import { Rng } from '../core/rng';
import { TAU, clamp } from '../core/math';

// Spectral Gerstner ocean. A fixed set of wave components (wavelength,
// direction, phase) is created once; their amplitudes follow a
// Pierson-Moskowitz spectrum for the current wind, with long waves responding
// slowly so a sea "builds" and "lies down" over time. The exact same model is
// evaluated on the GPU (render/ocean) for displacement/shading and on the CPU
// here for buoyancy, so the ship floats on the water you see.
//
// Surface of an undisplaced point x0:
//   P(x0) = x0 + sum_i( -Q_i A_i D_i sin(th_i),  A_i cos(th_i) )
//   th_i  = k_i (D_i . x0) - w_i t + phi_i

export const GRAVITY = 9.81;
export const WAVE_COUNT = 48;
const WIND_WAVES = 40;
/** Values per component in the packed sampling tables (see WaveField.pack). */
const STRIDE = 14;
// Taylor coefficients for sin and cos on [-pi, pi], to x^19 and x^20: within
// 1e-9 there, and a third quicker than Math.sin and Math.cos in the loop that
// samples the sea thousands of times a step (plain arithmetic, so it's also
// the same in every engine).
const S1 = -1 / 6, S2 = 1 / 120, S3 = -1 / 5040, S4 = 1 / 362880, S5 = -1 / 39916800, S6 = 1 / 6227020800;
const S7 = -1 / 1307674368000, S8 = 1 / 355687428096000, S9 = -1 / 121645100408832000;
const C1 = -1 / 2, C2 = 1 / 24, C3 = -1 / 720, C4 = 1 / 40320, C5 = -1 / 3628800, C6 = 1 / 479001600;
const C7 = -1 / 87178291200, C8 = 1 / 20922789888000, C9 = -1 / 6402373705728000, C10 = 1 / 2432902008176640000;
const INV_TAU = 1 / TAU;
const LAMBDA_MAX = 320;
const LAMBDA_MIN = 2.4;
const PM_ALPHA = 0.0081;

export interface SeaConditions {
  /** Wind speed at 10 m, m/s. */
  windSpeed: number;
  /** Unit vector the wind blows toward (world XZ). */
  windX: number;
  windZ: number;
  /** Significant height of the background swell, m. */
  swellHeight: number;
  /** User / weather multiplier on wind-sea height. */
  waveScale: number;
  /** Crest sharpness target (0 = sinusoidal, ~1 = near-breaking). */
  chop: number;
}

export interface WaveSample {
  /** Height of the surface (world y). */
  height: number;
  /** Horizontal displacement of the surface point. */
  dx: number;
  dz: number;
  /** Unit surface normal. */
  nx: number;
  ny: number;
  nz: number;
  /** Water particle velocity at the surface. */
  vx: number;
  vy: number;
  vz: number;
  /** Surface Jacobian (< ~0.3 means folding crest / whitecap). */
  jacobian: number;
  /** Undisplaced (Lagrangian) coordinates of the sampled surface point. */
  x0: number;
  z0: number;
}

export function makeWaveSample(): WaveSample {
  return { height: 0, dx: 0, dz: 0, nx: 0, ny: 1, nz: 0, vx: 0, vy: 0, vz: 0, jacobian: 1, x0: 0, z0: 0 };
}

export class WaveField {
  readonly count = WAVE_COUNT;
  readonly dirX = new Float64Array(WAVE_COUNT);
  readonly dirZ = new Float64Array(WAVE_COUNT);
  readonly k = new Float64Array(WAVE_COUNT);
  readonly omega = new Float64Array(WAVE_COUNT);
  readonly wavelength = new Float64Array(WAVE_COUNT);
  readonly phase0 = new Float64Array(WAVE_COUNT);
  readonly amp = new Float64Array(WAVE_COUNT);
  readonly targetAmp = new Float64Array(WAVE_COUNT);
  /** Horizontal (choppy) amplitude Q_i * A_i. */
  readonly qa = new Float64Array(WAVE_COUNT);
  /** (omega*t - phi) mod TAU, kept in double precision. */
  readonly phaseT = new Float64Array(WAVE_COUNT);
  private readonly tau = new Float64Array(WAVE_COUNT);
  private readonly bandWidth = new Float64Array(WAVE_COUNT);
  private readonly swellWeight = new Float64Array(WAVE_COUNT);
  private readonly isSwell = new Uint8Array(WAVE_COUNT);
  /** Indices of components significant for rigid-body physics. */
  private active = new Int32Array(WAVE_COUNT);
  private activeCount = 0;
  /**
   * Everything sampleAt0 needs per component, worked out once per step and
   * laid out together: every component, and just the physics set.
   */
  private readonly packAll = new Float64Array(WAVE_COUNT * STRIDE);
  private readonly packPhys = new Float64Array(WAVE_COUNT * STRIDE);
  /** No crest stands higher than this above the mean surface (m): the sum of the amplitudes. */
  maxHeight = 0;

  time = 0;
  chop = 0;
  significantHeight = 0;
  peakWavelength = 0;

  /**
   * @param seed deterministic seed
   * @param seaAngle direction (radians, atan2(z, x)) the wind sea travels in
   * @param swellAngle direction the background swell travels in
   */
  /** Directions (atan2(z, x)) the wind sea and the swell were set running in; they keep them. */
  readonly seaAngle: number;
  readonly swellAngle: number;

  constructor(seed: number, seaAngle: number, swellAngle = seaAngle + 0.55) {
    this.seaAngle = seaAngle;
    this.swellAngle = swellAngle;
    const rng = new Rng(seed);
    const dlnL = Math.log(LAMBDA_MAX / LAMBDA_MIN) / WIND_WAVES;
    for (let i = 0; i < WIND_WAVES; i++) {
      const t = (i + rng.range(0.15, 0.85)) / WIND_WAVES;
      const lambda = LAMBDA_MAX * Math.pow(LAMBDA_MIN / LAMBDA_MAX, t);
      // Long waves are more directional; short wind chop spreads widely.
      const sigma = 0.3 + 0.75 * t;
      const a = seaAngle + clamp(rng.gauss() * sigma, -1.5, 1.5);
      this.init(i, lambda, a, rng.range(0, TAU));
      const w = this.omega[i]!;
      this.bandWidth[i] = w * 0.5 * dlnL;
      this.tau[i] = 2 + 55 * Math.pow(lambda / LAMBDA_MAX, 0.8);
    }
    let wsum = 0;
    for (let i = WIND_WAVES; i < WAVE_COUNT; i++) {
      const j = i - WIND_WAVES;
      const lambda = 95 + j * 26 + rng.range(-8, 8);
      const a = swellAngle + rng.gauss() * 0.12;
      this.init(i, lambda, a, rng.range(0, TAU));
      this.isSwell[i] = 1;
      const w = Math.exp(-Math.pow((lambda - 180) / 70, 2));
      this.swellWeight[i] = w;
      wsum += w;
      this.tau[i] = 30;
    }
    for (let i = WIND_WAVES; i < WAVE_COUNT; i++) this.swellWeight[i]! /= wsum;
  }

  private init(i: number, lambda: number, angle: number, phase: number): void {
    const k = TAU / lambda;
    this.wavelength[i] = lambda;
    this.k[i] = k;
    this.omega[i] = Math.sqrt(GRAVITY * k);
    this.dirX[i] = Math.cos(angle);
    this.dirZ[i] = Math.sin(angle);
    this.phase0[i] = phase;
  }

  /** Recompute target amplitudes for the given conditions. */
  private computeTargets(c: SeaConditions): void {
    const U = Math.max(0.6, c.windSpeed);
    const wp = (0.877 * GRAVITY) / U;
    let peak = 0;
    let peakS = 0;
    for (let i = 0; i < WIND_WAVES; i++) {
      const w = this.omega[i]!;
      const s = ((PM_ALPHA * GRAVITY * GRAVITY) / Math.pow(w, 5)) * Math.exp(-1.25 * Math.pow(wp / w, 4));
      const cosd = this.dirX[i]! * c.windX + this.dirZ[i]! * c.windZ;
      const dirW = cosd > 0 ? 0.14 + 0.86 * cosd * cosd : 0.14 * (1 + cosd);
      this.targetAmp[i] = Math.sqrt(2 * s * this.bandWidth[i]!) * Math.sqrt(dirW) * 1.3 * c.waveScale;
      if (s > peakS) {
        peakS = s;
        peak = this.wavelength[i]!;
      }
    }
    this.peakWavelength = peak;
    const m0 = Math.pow(c.swellHeight / 4, 2);
    for (let i = WIND_WAVES; i < WAVE_COUNT; i++) {
      this.targetAmp[i] = Math.sqrt(2 * m0 * this.swellWeight[i]!);
    }
  }

  /** Jump straight to the equilibrium sea for these conditions. */
  settle(c: SeaConditions): void {
    this.computeTargets(c);
    this.amp.set(this.targetAmp);
    this.finish(c);
  }

  update(dt: number, c: SeaConditions): void {
    this.time += dt;
    this.computeTargets(c);
    for (let i = 0; i < WAVE_COUNT; i++) {
      const a = 1 - Math.exp(-dt / this.tau[i]!);
      this.amp[i]! += (this.targetAmp[i]! - this.amp[i]!) * a;
    }
    this.finish(c);
  }

  private finish(c: SeaConditions): void {
    let slope = 0;
    let m0 = 0;
    for (let i = 0; i < WAVE_COUNT; i++) {
      slope += this.amp[i]! * this.k[i]!;
      m0 += this.amp[i]! * this.amp[i]! * 0.5;
    }
    this.chop = Math.min(1, c.chop / Math.max(slope, 1e-4));
    this.significantHeight = 4 * Math.sqrt(m0);
    this.activeCount = 0;
    let maxH = 0;
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.qa[i] = this.chop * this.amp[i]!;
      const p = (this.omega[i]! * this.time - this.phase0[i]!) % TAU;
      this.phaseT[i] = p < 0 ? p + TAU : p;
      maxH += this.amp[i]!;
      this.pack(this.packAll, i, i);
      if (this.amp[i]! > 0.004 && this.wavelength[i]! > 3.2) {
        this.pack(this.packPhys, this.activeCount, i);
        this.active[this.activeCount++] = i;
      }
    }
    this.maxHeight = maxH;
  }

  /** Component i's constants into slot `slot` of a sampling table. */
  private pack(t: Float64Array, slot: number, i: number): void {
    const o = slot * STRIDE;
    const Dx = this.dirX[i]!, Dz = this.dirZ[i]!, k = this.k[i]!, A = this.amp[i]!, qa = this.qa[i]!, w = this.omega[i]!;
    const qk = qa * k, h = A * w * 0.85;
    t[o] = k * Dx;
    t[o + 1] = k * Dz;
    t[o + 2] = this.phaseT[i]!;
    t[o + 3] = A;
    t[o + 4] = qa * Dx;
    t[o + 5] = qa * Dz;
    t[o + 6] = A * k * Dx;
    t[o + 7] = A * k * Dz;
    t[o + 8] = qk * Dx * Dx;
    t[o + 9] = qk * Dz * Dz;
    t[o + 10] = qk * Dx * Dz;
    t[o + 11] = A * w;
    t[o + 12] = h * Dx;
    t[o + 13] = h * Dz;
  }

  /**
   * Evaluate the surface at an undisplaced (Lagrangian) point.
   * `physicsOnly` skips tiny components (used by rigid-body sampling).
   */
  sampleAt0(x0: number, z0: number, out: WaveSample, physicsOnly = false): WaveSample {
    let y = 0, dx = 0, dz = 0;
    let dydx = 0, dydz = 0, jxx = 0, jzz = 0, jxz = 0;
    let vx = 0, vy = 0, vz = 0;
    // Per component: height, horizontal displacement, slope, the displacement's
    // Jacobian and the orbital velocity, from the constants packed each step.
    const P = physicsOnly ? this.packPhys : this.packAll;
    const end = (physicsOnly ? this.activeCount : WAVE_COUNT) * STRIDE;
    for (let o = 0; o < end; o += STRIDE) {
      const th = P[o]! * x0 + P[o + 1]! * z0 - P[o + 2]!;
      const r = th - TAU * Math.round(th * INV_TAU);
      const r2 = r * r;
      const s = r * (1 + r2 * (S1 + r2 * (S2 + r2 * (S3 + r2 * (S4 + r2 * (S5 + r2 * (S6 + r2 * (S7 + r2 * (S8 + r2 * S9)))))))));
      const c = 1 + r2 * (C1 + r2 * (C2 + r2 * (C3 + r2 * (C4 + r2 * (C5 + r2 * (C6 + r2 * (C7 + r2 * (C8 + r2 * (C9 + r2 * C10)))))))));
      y += P[o + 3]! * c;
      dx -= P[o + 4]! * s;
      dz -= P[o + 5]! * s;
      dydx -= P[o + 6]! * s;
      dydz -= P[o + 7]! * s;
      jxx -= P[o + 8]! * c;
      jzz -= P[o + 9]! * c;
      jxz -= P[o + 10]! * c;
      vy += P[o + 11]! * s;
      vx += P[o + 12]! * c;
      vz += P[o + 13]! * c;
    }
    const nx = dydz * jxz - (1 + jzz) * dydx;
    const ny = (1 + jzz) * (1 + jxx) - jxz * jxz;
    const nz = jxz * dydx - dydz * (1 + jxx);
    const inv = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz);
    out.height = y;
    out.dx = dx;
    out.dz = dz;
    out.nx = nx * inv;
    out.ny = ny * inv;
    out.nz = nz * inv;
    out.vx = vx;
    out.vy = vy;
    out.vz = vz;
    out.jacobian = ny;
    out.x0 = x0;
    out.z0 = z0;
    return out;
  }

  /**
   * Evaluate the surface above world point (x, z) by inverting the horizontal
   * Gerstner displacement with fixed-point iteration. Pass the previous
   * result's x0/z0 as a warm start to converge in fewer iterations.
   */
  sample(x: number, z: number, out: WaveSample, physicsOnly = false, iterations = 3, warmX0?: number, warmZ0?: number): WaveSample {
    let x0 = warmX0 ?? x;
    let z0 = warmZ0 ?? z;
    for (let it = 0; it < iterations; it++) {
      this.sampleAt0(x0, z0, out, physicsOnly);
      x0 = x - out.dx;
      z0 = z - out.dz;
    }
    return this.sampleAt0(x0, z0, out, physicsOnly);
  }

  /** Height only, via the same inversion (convenience for tests/props). */
  heightAt(x: number, z: number, physicsOnly = false): number {
    return this.sample(x, z, scratch, physicsOnly).height;
  }

  /**
   * Pack GPU uniforms for an ocean mesh centred at world (ox, oz). Phases are
   * rebased to that origin in double precision so shaders only ever see small
   * local coordinates.
   *   a[i] = (Dx, Dz, k, A)       b[i] = (phase, Q*A, lambda, omega)
   */
  packUniforms(ox: number, oz: number, a: Float32Array, b: Float32Array): void {
    for (let i = 0; i < WAVE_COUNT; i++) {
      const k = this.k[i]!;
      let c = (k * (this.dirX[i]! * ox + this.dirZ[i]! * oz) - this.phaseT[i]!) % TAU;
      if (c < 0) c += TAU;
      a[i * 4] = this.dirX[i]!;
      a[i * 4 + 1] = this.dirZ[i]!;
      a[i * 4 + 2] = k;
      a[i * 4 + 3] = this.amp[i]!;
      b[i * 4] = c;
      b[i * 4 + 1] = this.qa[i]!;
      b[i * 4 + 2] = this.wavelength[i]!;
      b[i * 4 + 3] = this.omega[i]!;
    }
  }
}

const scratch = makeWaveSample();
